import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '@stormwater-software/lib-ui';
import { customDashboard } from '../../../../utils/api/dashboard';

export type PrecipitationRow = {
  projectId: number;
  projectName: string;
  clientName: string | null;
  day: string;
  rain: number;
  snow: number;
  isManual: boolean;
  rainThreshold: number | null;
  snowThreshold: number | null;
  isOverThreshold?: boolean;
};

interface Props {
  show: boolean;
  onClose: () => void;
  day: string;
  dateLabel: string;
  /** The over-threshold rows the grid already has, shown until the full day arrives. */
  overThreshold: PrecipitationRow[];
  path: string;
  selectionParams: Record<string, unknown>;
}

const amount = (value: number) => Number(value ?? 0).toFixed(2);

/**
 * Both lists are capped and grow in steps, matching the filter panel's "+N more".
 *
 * Neither is short: measured over one division-month, the busiest day held **161
 * projects over threshold and 87 under**, and the longest under-threshold list was
 * 212. Capping only the muted group — the obvious target — would still have left a
 * 161-row modal.
 */
const PAGE = 10;

/**
 * A day already fetched is not fetched again.
 *
 * Keyed by division, day and selection — all three. This cache lives on a calendar
 * cell, and those survive both a division change and a filter change.
 *
 * Reopening a day is a plausible way to use this — comparing one against another — and
 * a 304 is not cheap on these endpoints: the server runs the whole CTE, builds the
 * response, hashes it and discards it. Measured reopening the same day: 38ms then 59ms
 * for nothing new.
 *
 * Same policy as the month cache in `Calendar.jsx`. A day is only treated as settled
 * once it is old enough to be over in every timezone — see `settledBefore` — and
 * anything more recent expires, because a reading can still land while the dashboard
 * is open.
 */
const TODAY_TTL_MS = 60 * 1000;
const MAX_CACHED_DAYS = 12;

/**
 * The first date old enough to be settled, in *any* timezone.
 *
 * Readings are bucketed in each project's own timezone, but the browser only knows its
 * own. Comparing against the browser's today marks a day settled — and caches it for
 * the session — while a project further west is still living through it, so readings
 * added later in that project's day would never appear.
 *
 * Timezone offsets span about 26 hours end to end, so anything two days behind the
 * browser is over everywhere. More recent days fall back to the TTL.
 */
const settledBefore = () => {
  const cutoff = new Date();

  cutoff.setDate(cutoff.getDate() - 2);

  return `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(
    cutoff.getDate(),
  ).padStart(2, '0')}`;
};

/**
 * SW2-2338 — every project's rainfall for one day.
 *
 * Two things the design settles, both easy to get backwards:
 *
 * - **The amount that fell is the prominent figure**; the threshold sits under it as
 *   context. An early draft boxed the threshold, which made it the loudest number on
 *   the row and read as though it were the rainfall.
 * - **Under-threshold projects are listed too**, muted, below. That answers "did it
 *   rain anywhere else" without changing when the icon appears — the icon still means
 *   "met or surpassed", which is the widget's original definition.
 *
 * The full day is fetched on open rather than shipped with the month: a division-month
 * is 8,180 project-days against the 816 that are over threshold, and opening a day is
 * rare.
 */
const PrecipitationDayModal: React.FC<Props> = ({
  show,
  onClose,
  day,
  dateLabel,
  overThreshold,
  path,
  selectionParams,
}) => {
  const [rows, setRows] = useState<PrecipitationRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [overShown, setOverShown] = useState(PAGE);
  const [underShown, setUnderShown] = useState(PAGE);

  // A ref, not state: writing to it must not trigger a render.
  const dayCache = useRef(new Map<string, { projects: PrecipitationRow[]; at: number }>());

  useEffect(() => {
    if (!show) return undefined;

    let cancelled = false;

    setFailed(false);
    // A different day starts from the top, not wherever the last one was expanded to.
    setOverShown(PAGE);
    setUnderShown(PAGE);

    // `path` is in the key because it carries the division id, and this cache lives on
    // a calendar cell that survives a division change. Without it, the same date with
    // no project selection would key identically across divisions and serve the
    // previous one's projects back.
    const cacheKey = JSON.stringify([path, day, selectionParams]);
    const hit = dayCache.current.get(cacheKey);
    const isSettled = day < settledBefore();

    if (hit && (isSettled || Date.now() - hit.at < TODAY_TTL_MS)) {
      setRows(hit.projects);

      return undefined;
    }

    setRows(null);

    customDashboard
      .fetchWithParams(path, { ...selectionParams, day })
      .then((response: { projects: PrecipitationRow[] }) => {
        if (cancelled) return;

        const projects = response?.projects ?? [];

        // Oldest out first; a plain FIFO is enough, the bound only exists to stop a
        // long session holding every day of every selection.
        if (dayCache.current.size >= MAX_CACHED_DAYS) {
          dayCache.current.delete(dayCache.current.keys().next().value as string);
        }

        dayCache.current.set(cacheKey, { projects, at: Date.now() });
        setRows(projects);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, day, path, JSON.stringify(selectionParams)]);

  if (!show) return null;

  // Until the full day lands, the rows the grid already holds are shown rather than a
  // spinner — they are the ones the user clicked to see.
  const known = rows ?? overThreshold.map((r) => ({ ...r, isOverThreshold: true }));
  const over = known.filter((r) => r.isOverThreshold !== false);
  const under = known.filter((r) => r.isOverThreshold === false);

  const more = (
    total: number,
    shown: number,
    reveal: (n: number) => void,
    label: string,
  ) =>
    total > shown ? (
      <button
        type='button'
        className='precipitation-day__more'
        onClick={() => reveal(shown + PAGE)}
      >
        Show {Math.min(PAGE, total - shown)} more {label} ({total - shown} remaining)
      </button>
    ) : null;

  const row = (r: PrecipitationRow, muted: boolean) => (
    <li
      className={`precipitation-day__item${muted ? ' precipitation-day__item--under' : ''}`}
      key={r.projectId}
    >
      <div className='precipitation-day__project'>{r.projectName}</div>
      {r.clientName && (
        <div className='precipitation-day__client'>{r.clientName}</div>
      )}
      <dl className='precipitation-day__amounts'>
        <div>
          <dt>Rain</dt>
          <dd>
            {amount(r.rain)}&Prime;
            <span className='precipitation-day__threshold'>
              {r.rainThreshold == null
                ? 'no threshold set'
                : `threshold ${amount(r.rainThreshold)}″`}
            </span>
          </dd>
        </div>
        <div>
          <dt>Snow</dt>
          <dd>
            {amount(r.snow)}&Prime;
            <span className='precipitation-day__threshold'>
              {r.snowThreshold == null
                ? 'no threshold set'
                : `threshold ${amount(r.snowThreshold)}″`}
            </span>
          </dd>
        </div>
      </dl>
      {r.isManual && (
        <div className='precipitation-day__manual'>Manually recorded reading</div>
      )}
    </li>
  );

  return (
    <Modal
      show={show}
      title={<span className='calendar-day-modal__title'>{dateLabel}</span>}
      handleClose={onClose}
      hideButtons
      dismissOnBgClick
      classes='calendar-day-modal-shell precipitation-day-shell'
    >
      <div className='precipitation-day'>
        <p className='precipitation-day__count'>
          {over.length} {over.length === 1 ? 'project' : 'projects'} over threshold
        </p>

        <ul className='precipitation-day__list'>
          {over.slice(0, overShown).map((r) => row(r, false))}
        </ul>
        {more(over.length, overShown, setOverShown, 'over threshold')}

        {failed && (
          <p className='precipitation-day__note'>
            Could not load the other projects for this day.
          </p>
        )}

        {rows === null && !failed && (
          <p className='precipitation-day__note'>Loading the rest of the day…</p>
        )}

        {under.length > 0 && (
          <>
            <p className='precipitation-day__subhead'>
              {under.length} other {under.length === 1 ? 'project' : 'projects'} with a
              reading, under threshold
            </p>
            <ul className='precipitation-day__list'>
              {under.slice(0, underShown).map((r) => row(r, true))}
            </ul>
            {more(under.length, underShown, setUnderShown, 'under threshold')}
          </>
        )}
      </div>
    </Modal>
  );
};

export default PrecipitationDayModal;
